import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Card, Btn, Empty, Badge } from "../components/UI";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { FLabel, FInput, FSelect } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { isLateEntry, LateEntryChip } from "../lib/lateEntry";
import { Plus, Eye, Pencil, Trash2, Download, Upload, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const Th = ({ children }) => <th className="bg-[#FDFCF8] border-y border-[#EFEBE0] text-[#4E342E] font-semibold py-3 px-4 whitespace-nowrap text-left text-sm">{children}</th>;
export const Td = ({ children, className = "" }) => <td className={`border-b border-[#EFEBE0] py-3 px-4 text-sm ${className}`}>{children}</td>;

function PanelShell({ title, filter, action, children, empty, count }) {
  return (
    <Card data-testid="chart-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-3">
        <h3 className="font-heading font-bold text-[#3E2723]">{title}</h3>
        <div className="flex items-center gap-2">{filter}{action}</div>
      </div>
      <div className="overflow-x-auto">{count === 0 ? <Empty text={empty} /> : children}</div>
    </Card>
  );
}

const IconBtn = ({ testId, label, onClick, danger, children }) => (
  <button type="button" data-testid={testId} aria-label={label} title={label} onClick={onClick}
    className={`h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center ${danger ? "hover:bg-[#FFEBEE] hover:text-[#C62828]" : "hover:bg-[#EDE7DC]"}`}>{children}</button>
);

// ---------------- Vitals ----------------
const VITALS_EMPTY = { date: dayjs().format("YYYY-MM-DD"), time: dayjs().format("HH:mm"), bp_systolic: "", bp_diastolic: "", pulse: "", pulse_pattern: "", respiration: "", respiration_pattern: "", temperature: "", temperature_method: "", weight_lbs: "", height_in: "", o2_saturation: "", pain_level: "", comments: "" };

export function VitalsPanel({ client, editable }) {
  const [rows, setRows] = useState([]);
  const [lists, setLists] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(VITALS_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get(`/clients/${client.id}/vitals`).then((r) => setRows(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/custom-lists").then((r) => setLists(r.data)).catch(() => {}); }, []);

  const bmi = (() => { const w = parseFloat(form.weight_lbs), h = parseFloat(form.height_in); return w > 0 && h > 0 ? (703 * w / (h * h)).toFixed(1) : ""; })();
  const bmiTone = bmi ? (bmi < 18.5 ? "text-[#1565C0]" : bmi < 25 ? "text-[#2E7D32]" : bmi < 30 ? "text-[#B84A00]" : "text-[#C62828]") : "";
  const bmiLabel = bmi ? (bmi < 18.5 ? "Underweight" : bmi < 25 ? "Normal" : bmi < 30 ? "Overweight" : "Obese") : "";

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/vitals/${editId}`, form);
      else await api.post(`/clients/${client.id}/vitals`, form);
      toast.success(editId ? "Vitals updated" : "Vitals recorded");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const doDelete = async () => { await api.delete(`/vitals/${del.id}`); toast.success("Vitals entry deleted"); setDel(null); load(); };
  const num = (k, label, ph, step = "1") => (
    <div><FLabel>{label}</FLabel><FInput data-testid={`vitals-${k}`} type="number" step={step} min="0" placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
  );

  return (
    <>
      <PanelShell title="Vitals" count={rows.length} empty="No vitals recorded for this client"
        action={editable && <Btn data-testid="add-vitals-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(VITALS_EMPTY); setEditId(null); setOpen(true); }}><Plus size={14} /> Record Vitals</Btn>}>
        <table className="w-full">
          <thead><tr><Th>Date</Th><Th>Time</Th><Th>BP</Th><Th>Pulse</Th><Th>Resp</Th><Th>Temp</Th><Th>Weight</Th><Th>Height</Th><Th>BMI</Th><Th>Recorded By</Th><Th>{""}</Th></tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} data-testid={`vitals-row-${v.id}`} className="hover:bg-[#FDFCF8]">
                <Td className="tabular-nums">{dayjs(v.date).format("MM/DD/YYYY")}</Td>
                <Td className="tabular-nums">{v.time}</Td>
                <Td className="tabular-nums font-semibold">{v.bp_systolic && v.bp_diastolic ? `${v.bp_systolic}/${v.bp_diastolic}` : "-"}</Td>
                <Td className="tabular-nums">{v.pulse ? `${v.pulse}${v.pulse_pattern ? ` (${v.pulse_pattern})` : ""}` : "-"}</Td>
                <Td className="tabular-nums">{v.respiration ? `${v.respiration}${v.respiration_pattern ? ` (${v.respiration_pattern})` : ""}` : "-"}</Td>
                <Td className="tabular-nums">{v.temperature ? `${v.temperature}°F${v.temperature_method ? ` ${v.temperature_method}` : ""}` : "-"}</Td>
                <Td className="tabular-nums">{v.weight_lbs ? `${v.weight_lbs} lbs` : "-"}</Td>
                <Td className="tabular-nums">{v.height_in ? `${v.height_in} in` : "-"}</Td>
                <Td className="tabular-nums font-bold">{v.bmi ?? "-"}</Td>
                <Td>{v.recorded_by}</Td>
                <Td>
                  <div className="flex gap-2 justify-end">
                    {editable && <IconBtn testId={`edit-vitals-${v.id}`} label="Edit" onClick={() => { setForm({ ...VITALS_EMPTY, ...Object.fromEntries(Object.keys(VITALS_EMPTY).map((k) => [k, v[k] ?? ""])) }); setEditId(v.id); setOpen(true); }}><Pencil size={13} /></IconBtn>}
                    {editable && <IconBtn testId={`delete-vitals-${v.id}`} label="Delete" danger onClick={() => setDel(v)}><Trash2 size={13} /></IconBtn>}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </PanelShell>

      <SlidePanel open={open} onClose={() => setOpen(false)} title={editId ? "Edit Vitals" : "Record Vitals"} subtitle={`${client.first_name} ${client.last_name}`} testId="vitals-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Date</FLabel><DateField testId="vitals-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} noFuture /></div>
            <div><FLabel>Time</FLabel><FInput data-testid="vitals-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
          </div>
          <div className="rounded-xl border border-[#F1EEE7] p-3 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wide text-[#84675B]">Blood Pressure</div>
            <div className="grid grid-cols-2 gap-3">{num("bp_systolic", "Systolic (mmHg)", "120")}{num("bp_diastolic", "Diastolic (mmHg)", "80")}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {num("pulse", "Pulse (bpm)", "72")}
            <div><FLabel>Pulse Pattern</FLabel><FSelect data-testid="vitals-pulse-pattern" value={form.pulse_pattern} onChange={(e) => setForm({ ...form, pulse_pattern: e.target.value })}><option value="">Select…</option>{(lists.vitals_pulse_patterns || []).map((p) => <option key={p}>{p}</option>)}</FSelect></div>
            {num("respiration", "Respiration (rpm)", "16")}
            <div><FLabel>Respiration Pattern</FLabel><FSelect data-testid="vitals-respiration-pattern" value={form.respiration_pattern} onChange={(e) => setForm({ ...form, respiration_pattern: e.target.value })}><option value="">Select…</option>{(lists.vitals_respiration_patterns || []).map((p) => <option key={p}>{p}</option>)}</FSelect></div>
            {num("temperature", "Temperature (°F)", "98.6", "0.1")}
            <div><FLabel>Temp Method</FLabel><FSelect data-testid="vitals-temperature-method" value={form.temperature_method} onChange={(e) => setForm({ ...form, temperature_method: e.target.value })}><option value="">Select…</option>{(lists.vitals_temperature_methods || []).map((p) => <option key={p}>{p}</option>)}</FSelect></div>
          </div>
          <div className="rounded-xl border border-[#F1EEE7] p-3 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wide text-[#84675B]">Body Measurements</div>
            <div className="grid grid-cols-2 gap-3">{num("weight_lbs", "Weight (lbs)", "165", "0.1")}{num("height_in", "Height (inches)", "68", "0.1")}</div>
            <div className="flex items-center justify-between rounded-lg bg-[#F5F2EB] px-3 py-2">
              <span className="text-xs font-bold text-[#5D4037]">BMI (auto-calculated)</span>
              <span data-testid="vitals-bmi-preview" className={`text-sm font-bold ${bmiTone}`}>{bmi ? `${bmi} · ${bmiLabel}` : "Enter weight & height"}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">{num("o2_saturation", "O2 Saturation (%)", "98")}{num("pain_level", "Pain Level (0-10)", "0")}</div>
          <div><FLabel>Comments</FLabel><textarea data-testid="vitals-comments" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} rows={2} className="w-full rounded-xl border border-[#E5DFD3] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="vitals-save-btn" type="submit">{editId ? "Save" : "Record"}</Btn>
          </div>
        </form>
      </SlidePanel>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} title="Delete vitals entry" description={del ? `Delete vitals recorded ${dayjs(del.date).format("MM/DD/YYYY")}?` : ""} />
    </>
  );
}

// ---------------- Clinical Assessments ----------------
export function AssessmentsChartPanel({ client }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  useEffect(() => { api.get("/assessments", { params: { client_id: client.id, ...(status ? { status } : {}) } }).then((r) => setRows(r.data)); }, [client.id, status]);
  return (
    <PanelShell title="Clinical Assessments" count={rows.length} empty="No assessments completed for this client yet"
      filter={<FSelect data-testid="assessments-status-filter" className="w-36 h-10" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option><option value="draft">Draft</option><option value="completed">Completed</option><option value="signed">Signed</option></FSelect>}
      action={<Btn data-testid="new-assessment-btn" variant="outline" className="rounded-lg" onClick={() => navigate("/clinical/assessments")}><Plus size={14} /> New Assessment</Btn>}>
      <table className="w-full">
        <thead><tr><Th>Assessment</Th><Th>Status</Th><Th>Created</Th><Th>By</Th><Th>{""}</Th></tr></thead>
        <tbody>{rows.map((a) => (
          <tr key={a.id} data-testid={`assessment-row-${a.id}`} className="hover:bg-[#FDFCF8]">
            <Td className="font-semibold text-[#3E2723]">{a.template_name}</Td>
            <Td><Badge value={a.status} /></Td>
            <Td className="tabular-nums">{dayjs(a.created_at).format("MM/DD/YYYY")}</Td>
            <Td>{a.created_by}</Td>
            <Td><div className="flex justify-end"><IconBtn testId={`open-assessment-${a.id}`} label="Open" onClick={() => navigate(`/clinical/assessments/${a.id}`)}><Eye size={13} /></IconBtn></div></Td>
          </tr>
        ))}</tbody>
      </table>
    </PanelShell>
  );
}

// ---------------- Treatment Plans ----------------
export function TreatmentPlansChartPanel({ client }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  useEffect(() => { api.get(`/clients/${client.id}/treatment-plans-all`).then((r) => setRows(r.data)); }, [client.id]);
  const filtered = status ? rows.filter((p) => p.status === status) : rows;
  return (
    <PanelShell title="Treatment Plans" count={filtered.length} empty="No treatment plans for this client yet"
      filter={<FSelect data-testid="plans-status-filter" className="w-36 h-10" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{[...new Set(rows.map((p) => p.status))].map((s) => <option key={s} value={s}>{s}</option>)}</FSelect>}
      action={<Btn data-testid="new-plan-btn" variant="outline" className="rounded-lg" onClick={() => navigate("/clinical/treatment-plans/new")}><Plus size={14} /> New Plan</Btn>}>
      <table className="w-full">
        <thead><tr><Th>Status</Th><Th>Created</Th><Th>Review / Due</Th><Th>{""}</Th></tr></thead>
        <tbody>{filtered.map((p) => (
          <tr key={p.id} data-testid={`plan-row-${p.id}`} className="hover:bg-[#FDFCF8]">
            <Td><Badge value={p.status} /></Td>
            <Td className="tabular-nums">{dayjs(p.created_at).format("MM/DD/YYYY")}</Td>
            <Td className="tabular-nums">{p.review_date || p.due_date || "-"}</Td>
            <Td><div className="flex justify-end"><IconBtn testId={`open-plan-${p.id}`} label="Open" onClick={() => navigate(`/clinical/treatment-plans/${p.id}`)}><Eye size={13} /></IconBtn></div></Td>
          </tr>
        ))}</tbody>
      </table>
    </PanelShell>
  );
}

// ---------------- Progress Notes ----------------
export function ProgressNotesChartPanel({ client }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  useEffect(() => { api.get("/progress-notes", { params: { client_id: client.id, ...(status ? { status } : {}) } }).then((r) => setRows(r.data.items || r.data)); }, [client.id, status]);
  return (
    <PanelShell title="Progress Notes" count={rows.length} empty="No progress notes for this client yet"
      filter={<FSelect data-testid="pnotes-status-filter" className="w-36 h-10" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option><option value="draft">Draft</option><option value="completed">Completed</option><option value="signed">Signed</option><option value="pending_cosign">Pending Co-sign</option></FSelect>}
      action={<Btn data-testid="new-pnote-btn" variant="outline" className="rounded-lg" onClick={() => navigate("/clinical/progress-notes/new")}><Plus size={14} /> New Note</Btn>}>
      <table className="w-full">
        <thead><tr><Th>Date</Th><Th>Service</Th><Th>Provider</Th><Th>Format</Th><Th>Status</Th><Th>{""}</Th></tr></thead>
        <tbody>{rows.map((n) => (
          <tr key={n.id} data-testid={`pnote-row-${n.id}`} className="hover:bg-[#FDFCF8]">
            <Td className="tabular-nums"><span className="flex items-center gap-1.5">{dayjs(n.date).format("MM/DD/YYYY")}{isLateEntry(n.date, n.created_at) && <LateEntryChip id={n.id} />}</span></Td>
            <Td>{n.service}</Td>
            <Td>{n.provider_name}</Td>
            <Td>{n.format}</Td>
            <Td><Badge value={n.status} /></Td>
            <Td><div className="flex justify-end"><IconBtn testId={`open-pnote-${n.id}`} label="Open" onClick={() => navigate(`/clinical/progress-notes/${n.id}`)}><Eye size={13} /></IconBtn></div></Td>
          </tr>
        ))}</tbody>
      </table>
    </PanelShell>
  );
}

// ---------------- Group Notes ----------------
export function GroupNotesPanel({ client }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/clients/${client.id}/group-notes`).then((r) => setRows(r.data)); }, [client.id]);
  return (
    <PanelShell title="Group Notes" count={rows.length} empty="This client has not participated in any group sessions">
      <table className="w-full">
        <thead><tr><Th>Date</Th><Th>Group</Th><Th>Type</Th><Th>Facilitator</Th><Th>Attendance</Th><Th>Client Response</Th><Th>{""}</Th></tr></thead>
        <tbody>{rows.map((g) => (
          <tr key={g.id} data-testid={`group-note-row-${g.id}`} className="hover:bg-[#FDFCF8]">
            <Td className="tabular-nums">{g.date ? dayjs(g.date).format("MM/DD/YYYY") : "-"}</Td>
            <Td className="font-semibold text-[#3E2723]">{g.name}</Td>
            <Td>{g.group_type || "-"}</Td>
            <Td>{g.facilitator || "-"}</Td>
            <Td>{g.attendance ? <Badge value={String(g.attendance)} /> : "-"}</Td>
            <Td className="max-w-[240px] truncate">{g.response || "-"}</Td>
            <Td><div className="flex justify-end"><IconBtn testId={`open-group-${g.id}`} label="Open group" onClick={() => navigate(`/group360/${g.id}`)}><ExternalLink size={13} /></IconBtn></div></Td>
          </tr>
        ))}</tbody>
      </table>
    </PanelShell>
  );
}

// ---------------- Behavior Tracking ----------------
const BEHAVIOR_EMPTY = { date: dayjs().format("YYYY-MM-DD"), behavior: "", category: "", severity: "2", antecedent: "", intervention: "" };
export function BehaviorChartPanel({ client, editable }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BEHAVIOR_EMPTY);
  const load = useCallback(() => api.get("/behavior-tracker", { params: { client_id: client.id } }).then((r) => setRows(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/behavior-tracker/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/behavior-tracker", { ...form, severity: Number(form.severity), client_id: client.id, client_name: `${client.first_name} ${client.last_name}` });
      toast.success("Behavior entry recorded"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const sevTone = (s) => (s >= 4 ? "bg-[#FFEBEE] text-[#C62828]" : s === 3 ? "bg-[#FFF3E0] text-[#B84A00]" : "bg-[#F5F2EB] text-[#7B6157]");
  return (
    <>
      <PanelShell title="Behavior Tracking" count={rows.length} empty="No behavior entries for this client"
        action={editable && <Btn data-testid="add-behavior-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(BEHAVIOR_EMPTY); setOpen(true); }}><Plus size={14} /> Log Behavior</Btn>}>
        <table className="w-full">
          <thead><tr><Th>Date</Th><Th>Behavior</Th><Th>Category</Th><Th>Severity</Th><Th>Antecedent</Th><Th>Intervention</Th><Th>Recorded By</Th></tr></thead>
          <tbody>{rows.map((b) => (
            <tr key={b.id} data-testid={`behavior-row-${b.id}`} className="hover:bg-[#FDFCF8]">
              <Td className="tabular-nums">{dayjs(b.date).format("MM/DD/YYYY")}</Td>
              <Td className="font-semibold text-[#3E2723]">{b.behavior}</Td>
              <Td>{b.category}</Td>
              <Td><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${sevTone(b.severity)}`}>Level {b.severity}</span></Td>
              <Td>{b.antecedent || "-"}</Td>
              <Td>{b.intervention || "-"}</Td>
              <Td>{b.recorded_by}</Td>
            </tr>
          ))}</tbody>
        </table>
      </PanelShell>
      <SlidePanel open={open} onClose={() => setOpen(false)} title="Log Behavior" subtitle={`${client.first_name} ${client.last_name}`} testId="behavior-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div><FLabel>Date</FLabel><DateField testId="behavior-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} noFuture /></div>
          <div><FLabel>Behavior observed</FLabel><FInput data-testid="behavior-text" value={form.behavior} onChange={(e) => setForm({ ...form, behavior: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Category</FLabel><FSelect data-testid="behavior-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}><option value="">Select…</option>{cats.map((c) => <option key={c}>{c}</option>)}</FSelect></div>
            <div><FLabel>Severity (1-5)</FLabel><FSelect data-testid="behavior-severity" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>{[1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>Level {s}</option>)}</FSelect></div>
          </div>
          <div><FLabel>Antecedent / trigger</FLabel><FInput value={form.antecedent} onChange={(e) => setForm({ ...form, antecedent: e.target.value })} /></div>
          <div><FLabel>Intervention</FLabel><FInput value={form.intervention} onChange={(e) => setForm({ ...form, intervention: e.target.value })} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4"><Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="behavior-save-btn" type="submit">Log Entry</Btn></div>
        </form>
      </SlidePanel>
    </>
  );
}

// ---------------- Collateral Contacts ----------------
const CC_EMPTY = { contact_name: "", relationship: "", organization: "", method: "Phone", date: dayjs().format("YYYY-MM-DD"), duration_minutes: "", roi_on_file: false, summary: "" };
export function CollateralPanel({ client, editable }) {
  const [rows, setRows] = useState([]);
  const [lists, setLists] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(CC_EMPTY);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get(`/clients/${client.id}/collateral-contacts`).then((r) => setRows(r.data)), [client.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/custom-lists").then((r) => setLists(r.data)).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/collateral-contacts/${editId}`, form);
      else await api.post(`/clients/${client.id}/collateral-contacts`, form);
      toast.success("Collateral contact saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const doDelete = async () => { await api.delete(`/collateral-contacts/${del.id}`); toast.success("Contact deleted"); setDel(null); load(); };
  return (
    <>
      <PanelShell title="Collateral Contacts" count={rows.length} empty="No collateral contacts documented"
        action={editable && <Btn data-testid="add-collateral-btn" variant="outline" className="rounded-lg" onClick={() => { setForm(CC_EMPTY); setEditId(null); setOpen(true); }}><Plus size={14} /> Log Contact</Btn>}>
        <table className="w-full">
          <thead><tr><Th>Date</Th><Th>Contact</Th><Th>Relationship</Th><Th>Method</Th><Th>Duration</Th><Th>ROI</Th><Th>Summary</Th><Th>{""}</Th></tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} data-testid={`collateral-row-${c.id}`} className="hover:bg-[#FDFCF8]">
              <Td className="tabular-nums">{dayjs(c.date).format("MM/DD/YYYY")}</Td>
              <Td className="font-semibold text-[#3E2723]">{c.contact_name}{c.organization ? <span className="block text-[11px] font-normal text-[#84675B]">{c.organization}</span> : null}</Td>
              <Td>{c.relationship || "-"}</Td>
              <Td>{c.method}</Td>
              <Td className="tabular-nums">{c.duration_minutes ? `${c.duration_minutes} min` : "-"}</Td>
              <Td>{c.roi_on_file ? <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]">ON FILE</span> : <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]">MISSING</span>}</Td>
              <Td className="max-w-[220px] truncate">{c.summary || "-"}</Td>
              <Td><div className="flex gap-2 justify-end">
                {editable && <IconBtn testId={`edit-collateral-${c.id}`} label="Edit" onClick={() => { setForm({ ...CC_EMPTY, ...Object.fromEntries(Object.keys(CC_EMPTY).map((k) => [k, c[k] ?? ""])) }); setEditId(c.id); setOpen(true); }}><Pencil size={13} /></IconBtn>}
                {editable && <IconBtn testId={`delete-collateral-${c.id}`} label="Delete" danger onClick={() => setDel(c)}><Trash2 size={13} /></IconBtn>}
              </div></Td>
            </tr>
          ))}</tbody>
        </table>
      </PanelShell>
      <SlidePanel open={open} onClose={() => setOpen(false)} title={editId ? "Edit Collateral Contact" : "Log Collateral Contact"} subtitle={`${client.first_name} ${client.last_name}`} testId="collateral-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Contact name</FLabel><FInput data-testid="collateral-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} required /></div>
            <div><FLabel>Relationship</FLabel><FInput data-testid="collateral-relationship" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="e.g. Probation officer" /></div>
          </div>
          <div><FLabel>Organization</FLabel><FInput value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><FLabel>Method</FLabel><FSelect data-testid="collateral-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>{(lists.collateral_methods || ["Phone", "In Person", "Email"]).map((m) => <option key={m}>{m}</option>)}</FSelect></div>
            <div><FLabel>Date</FLabel><DateField testId="collateral-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} noFuture /></div>
            <div><FLabel>Duration (min)</FLabel><FInput type="number" min="0" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-[#3E2723] cursor-pointer">
            <input data-testid="collateral-roi" type="checkbox" checked={!!form.roi_on_file} onChange={(e) => setForm({ ...form, roi_on_file: e.target.checked })} className="h-4 w-4 accent-[#4E342E]" />
            Release of Information (ROI) on file for this contact
          </label>
          {!form.roi_on_file && <p className="rounded-lg bg-[#FFF8E1] border border-[#FFE0B2] px-3 py-2 text-[11px] text-[#6D4C41]">Reminder: HIPAA 45 CFR 164.508 requires a signed authorization before disclosing PHI to third parties, except as permitted for treatment, payment, or operations.</p>}
          <div><FLabel>Contact summary</FLabel><textarea data-testid="collateral-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} className="w-full rounded-xl border border-[#E5DFD3] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4"><Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="collateral-save-btn" type="submit">{editId ? "Save" : "Log Contact"}</Btn></div>
        </form>
      </SlidePanel>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} title="Delete collateral contact" description={del ? `Delete contact with ${del.contact_name} dated ${dayjs(del.date).format("MM/DD/YYYY")}?` : ""} />
    </>
  );
}

// ---------------- Documents (Scanned / Legacy) ----------------
export function DocumentsPanel({ client, category, editable }) {
  const [rows, setRows] = useState([]);
  const [del, setDel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const label = category === "legacy" ? "Legacy Documents" : "Scanned Documents";
  const load = useCallback(() => api.get(`/clients/${client.id}/documents`, { params: { category } }).then((r) => setRows(r.data)), [client.id, category]);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds the 5 MB limit"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/clients/${client.id}/documents`, { name: file.name, category, content_type: file.type, data: reader.result });
        toast.success("Document uploaded"); load();
      } catch (err) { toast.error(apiError(err)); }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };
  const download = async (d) => {
    const r = await api.get(`/client-documents/${d.id}/download`);
    const a = document.createElement("a");
    a.href = r.data.data;
    a.download = r.data.name;
    a.click();
  };
  const doDelete = async () => { await api.delete(`/client-documents/${del.id}`); toast.success("Document deleted"); setDel(null); load(); };
  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  return (
    <>
      <PanelShell title={label} count={rows.length} empty={`No ${label.toLowerCase()} uploaded for this client`}
        action={editable && (
          <label data-testid={`upload-${category}-btn`} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#D7CCC8] px-4 py-2 text-sm font-semibold text-[#5D4037] hover:bg-[#F5F2EB] ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload Document"}
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tiff,.doc,.docx" className="hidden" onChange={onFile} />
          </label>
        )}>
        <table className="w-full">
          <thead><tr><Th>Document</Th><Th>Size</Th><Th>Uploaded By</Th><Th>Uploaded</Th><Th>{""}</Th></tr></thead>
          <tbody>{rows.map((d) => (
            <tr key={d.id} data-testid={`document-row-${d.id}`} className="hover:bg-[#FDFCF8]">
              <Td><span className="flex items-center gap-2 font-semibold text-[#3E2723]"><FileText size={15} className="text-[#7B6157] shrink-0" />{d.name}</span></Td>
              <Td className="tabular-nums">{fmtSize(d.size_bytes || 0)}</Td>
              <Td>{d.uploaded_by}</Td>
              <Td className="tabular-nums">{dayjs(d.created_at).format("MM/DD/YYYY h:mm A")}</Td>
              <Td><div className="flex gap-2 justify-end">
                <IconBtn testId={`download-document-${d.id}`} label="Download" onClick={() => download(d)}><Download size={13} /></IconBtn>
                {editable && <IconBtn testId={`delete-document-${d.id}`} label="Delete" danger onClick={() => setDel(d)}><Trash2 size={13} /></IconBtn>}
              </div></Td>
            </tr>
          ))}</tbody>
        </table>
      </PanelShell>
      <p className="mt-2 px-1 text-[10px] text-[#84675B]">Uploads are limited to 5 MB per file, stored encrypted, and every view or download is audit-logged (HIPAA 45 CFR 164.312(b)). Only upload documents relevant to this client's care.</p>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} title="Delete document" description={del ? `Permanently delete "${del.name}"? This cannot be undone.` : ""} />
    </>
  );
}

// ---------------- ASAM ----------------
export function AsamChartPanel({ client }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/clients/${client.id}/asam`).then((r) => setRows(r.data)).catch(() => {}); }, [client.id]);
  return (
    <>
      <PanelShell title="ASAM Assessments" count={rows.length} empty="No ASAM level-of-care assessments recorded for this client">
        <table className="w-full">
          <thead><tr><Th>Date</Th><Th>Recommended Level</Th><Th>Status</Th><Th>Completed By</Th></tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.id} data-testid={`asam-row-${a.id}`} className="hover:bg-[#FDFCF8]">
              <Td className="tabular-nums">{a.date ? dayjs(a.date).format("MM/DD/YYYY") : dayjs(a.created_at).format("MM/DD/YYYY")}</Td>
              <Td className="font-semibold text-[#3E2723]">{a.recommended_level || a.level_of_care || "-"}</Td>
              <Td><Badge value={a.status || "draft"} /></Td>
              <Td>{a.completed_by || a.created_by || "-"}</Td>
            </tr>
          ))}</tbody>
        </table>
      </PanelShell>
      <p className="mt-2 px-1 text-[10px] text-[#84675B]">The full ASAM criteria workflow (dimensional ratings and level-of-care builder) arrives with COCOA Version 2.</p>
    </>
  );
}

// ---------------- Intake ----------------
export function IntakeChartPanel({ client, intake }) {
  const navigate = useNavigate();
  return (
    <Card data-testid="chart-panel" className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-[#3E2723]">Intake</h3>
        {!intake && <Btn data-testid="new-intake-btn" variant="outline" className="rounded-lg" onClick={() => navigate("/engagement/intake/new")}><Plus size={14} /> Start Intake</Btn>}
      </div>
      {!intake ? <div className="mt-4"><Empty text="No intake form on file for this client" /></div> : (
        <div className="mt-4 flex flex-wrap items-center gap-6 rounded-2xl border border-[#F1EEE7] p-5">
          <div>
            <div className="text-xs text-[#84675B]">Status</div>
            <Badge value={intake.status || "draft"} />
          </div>
          <div>
            <div className="text-xs text-[#84675B]">Started</div>
            <div className="text-sm font-semibold text-[#3E2723] tabular-nums">{intake.created_at ? dayjs(intake.created_at).format("MM/DD/YYYY") : "-"}</div>
          </div>
          <div>
            <div className="text-xs text-[#84675B]">Last updated</div>
            <div className="text-sm font-semibold text-[#3E2723] tabular-nums">{intake.updated_at ? dayjs(intake.updated_at).format("MM/DD/YYYY") : "-"}</div>
          </div>
          <Btn data-testid="open-intake-btn" variant="outline" className="ml-auto rounded-lg" onClick={() => navigate(`/engagement/intake/${intake.id}/edit`)}><Eye size={14} /> Open Intake Form</Btn>
        </div>
      )}
    </Card>
  );
}
